import { queryParamsFromUrlString } from '@/core/apis/browser_api';
import { CookieLibrary } from '@/core/apis/browser_storage_api/cookie_lib/cookie_lib';
import {
  TBrowserApiData,
  TEventInputData,
} from '@/core/constants/event_types/additional_types';
import {
  EDiscountTarget,
  EDiscountTargetType,
  EDiscountType,
  EDiscountValueType,
  TCartData,
  TDiscount,
  TMableProduct,
} from '@/core/constants/event_types/extended_mable_event';
import { EURO_CURRENCY_CODE } from '@/core/constants/fallbacks/fallbacks';
import { ICustomerDataFields } from '@/core/data_handlers/customer_data_new/types';
import { Logger } from '@/shared/logger/logger';
import {
  CpxCartLine,
  CpxCheckout,
  CpxDiscountAllocation,
  CpxDiscountApplication,
  CpxLineItem,
  CpxMerchandise,
  ShpyCpxHandlerInput,
} from '@/sources/bundles/wtag-shpfy-cpx/shopify_custom_pixel_types/cpx_payload_types';

import { CpxCustomerDataHandler } from './cpx_customer_data_handler';

export abstract class GenericShpyCpxTrigger {
  transformToWtagInput(
    cpx_input_payload: ShpyCpxHandlerInput,
  ): PromiseLike<TEventInputData> | TEventInputData {
    throw new Error(
      `Method not implemented. cpx_input_payload:  ${!!cpx_input_payload}`,
    );
  }
  static getPaymentMethod(deps: ShpyCpxHandlerInput) {
    const checkoutData = deps?.event?.data?.checkout?.transactions || [];
    return (
      checkoutData?.map(
        e =>
          `${e.gateway}_${e.paymentMethod?.name || ''}_${e.paymentMethod?.type || ''}`,
      ) || []
    );
  }

  static getPriceFields({ deps }: { deps: ShpyCpxHandlerInput }) {
    try {
      const checkoutData = deps?.event?.data?.checkout;
      const value = this.getCustomPixelTotalPrice({ deps });
      const currency = this.getCustomPixelCurrencyCode({ deps });

      return {
        currency,
        total_discounted_price: value || 0,
        total_discounts: checkoutData?.discountsAmount?.amount || 0,
        total_line_items_price: checkoutData?.subtotalPrice?.amount || 0,
        total_price: value || 0,
        total_shipping_price: checkoutData?.shippingLine?.price?.amount || 0,
        total_tax: checkoutData?.totalTax?.amount || 0,
      };
    } catch (error) {
      Logger.error('Error in getPriceFields', {
        error,
        input: deps,
      });
      return {
        currency: EURO_CURRENCY_CODE,
        total_discounted_price: 0,
        total_discounts: 0,
        total_line_items_price: 0,
        total_price: 0,
        total_shipping_price: 0,
        total_tax: 0,
      };
    }
  }

  static getCustomPixelCurrencyCode({
    deps,
  }: {
    deps: ShpyCpxHandlerInput;
  }): string {
    try {
      const eventData = deps?.event?.data;
      const shopData = deps?.init?.data?.shop;

      // Enhanced fallback chain with null checks
      return (
        eventData?.checkout?.currencyCode ||
        eventData?.cartLine?.merchandise?.price?.currencyCode ||
        eventData?.cart?.cost?.totalAmount?.currencyCode ||
        shopData?.paymentSettings?.currencyCode ||
        EURO_CURRENCY_CODE
      );
    } catch (error) {
      Logger.error('Error in getCustomPixelCurrencyCode', {
        error,
        input: deps,
      });
      return EURO_CURRENCY_CODE;
    }
  }

  static getCustomPixelTotalPrice({
    deps,
  }: {
    deps: ShpyCpxHandlerInput;
  }): number {
    try {
      const eventData = deps?.event?.data;

      // Enhanced fallback chain with null checks
      return (
        eventData?.checkout?.totalPrice?.amount ||
        eventData?.cartLine?.merchandise?.price?.amount ||
        eventData?.cart?.cost?.totalAmount?.amount ||
        eventData?.productVariant?.price?.amount ||
        0
      );
    } catch (error) {
      Logger.error('Error in getCustomPixelTotalPrice', {
        error,
        input: deps,
      });
      return 0;
    }
  }

  static getProductsForCpx(cpx_payload: ShpyCpxHandlerInput): TMableProduct[] {
    const fallbackCurrency = this.getCustomPixelCurrencyCode({
      deps: cpx_payload,
    });
    const { cartLine } = cpx_payload?.event?.data || {};
    const checkoutData = cpx_payload?.event?.data?.checkout;
    const productViewed = cpx_payload?.event?.data?.productVariant;
    if (productViewed && Object.keys(productViewed).length > 0) {
      return this.convertCustomPixelMerchandiseToMableProducts(
        productViewed,
        fallbackCurrency,
      );
    }
    if (cartLine && Object.keys(cartLine).length > 0) {
      return this.convertCustomPixelCartLineToMableProducts(
        cartLine,
        fallbackCurrency,
      );
    }
    if (checkoutData && checkoutData.lineItems.length > 0) {
      return this.convertCustomPixelProductsToMableProducts(
        checkoutData.lineItems,
        fallbackCurrency,
      );
    }

    return [];
  }

  static convertCustomPixelProductsToMableProducts(
    lineItems: CpxLineItem[],
    fallBackCurrency: string,
  ): TMableProduct[] {
    return lineItems.map(li => {
      const { discounts, totalDiscount } =
        GenericShpyCpxTrigger.getDiscountsForCpxLineItem(li);
      const currProductInfo = li.variant.product;
      const lineItemPriceInfo = li.variant.price;
      const product_id = String(currProductInfo.id || '');
      const variant_id = String(li.variant.id || '');
      const sku = currProductInfo.sku || li.variant.sku || '';
      return {
        br: currProductInfo.vendor || '',
        cat: currProductInfo.type || '',
        cur: lineItemPriceInfo.currencyCode || fallBackCurrency,
        discounts,
        id: product_id,
        ids: { pid: product_id, pnu: '', sku, vid: variant_id },
        nam: currProductInfo.title || '',
        prc: String(lineItemPriceInfo.amount),
        qty: li.quantity || 0,
        totalDiscount,
        var: li.variant.title || '',
      };
    });
  }

  static convertCustomPixelCartLineToMableProducts(
    cartLine: CpxCartLine,
    fallBackCurrency: string,
  ): TMableProduct[] {
    const cartLineMerchandise = cartLine.merchandise;
    const cartLineMerchandiseProduct = cartLineMerchandise.product;
    const product_id = String(cartLineMerchandiseProduct.id || '');
    const sku = cartLineMerchandise.sku || '';
    const variant_id = String(cartLineMerchandise.id || '') || '';
    return [
      {
        br: cartLineMerchandiseProduct.vendor || '',
        cat: cartLineMerchandiseProduct.type || '',
        cur: cartLineMerchandise.price.currencyCode || fallBackCurrency,
        discounts: [],
        id: product_id,
        ids: { pid: product_id, pnu: '', sku, vid: variant_id },
        nam: cartLineMerchandiseProduct.title || '',
        prc: String(Number(cartLineMerchandise.price.amount || 0) || 0),
        qty: cartLine.quantity || 0,
        totalDiscount: 0,
        var: cartLineMerchandise.title || '',
      },
    ];
  }

  static convertCustomPixelMerchandiseToMableProducts(
    productViewed: CpxMerchandise,
    fallBackCurrency: string,
  ): TMableProduct[] {
    const cartLineMerchandise = productViewed;
    const cartLineMerchandiseProduct = cartLineMerchandise.product;
    const product_id = String(cartLineMerchandiseProduct.id || '');
    const variant_id = String(cartLineMerchandise.id || '') || '';
    const sku = cartLineMerchandise.sku || '';
    return [
      {
        br: cartLineMerchandiseProduct.vendor || '',
        cat: cartLineMerchandiseProduct.type || '',
        cur: cartLineMerchandise.price.currencyCode || fallBackCurrency,
        discounts: [],
        id: product_id,
        ids: { pid: product_id, pnu: '', sku, vid: variant_id },
        nam: cartLineMerchandiseProduct.title || '',
        prc: String(Number(cartLineMerchandise.price.amount || 0) || 0),
        qty: 1,
        totalDiscount: 0,
        var: cartLineMerchandiseProduct.title,
      },
    ];
  }

  static getCartData(cpx_input_payload: ShpyCpxHandlerInput): TCartData {
    try {
      const price_fields = GenericShpyCpxTrigger.getPriceFields({
        deps: cpx_input_payload,
      });
      const products =
        GenericShpyCpxTrigger.getProductsForCpx(cpx_input_payload);
      const checkout = cpx_input_payload.event?.data?.checkout;
      const { discounts, totalDiscount } =
        GenericShpyCpxTrigger.getDiscountsForCpxCheckout(checkout);

      // Get currency with proper fallback chain
      const currency = GenericShpyCpxTrigger.getCustomPixelCurrencyCode({
        deps: cpx_input_payload,
      });

      // Calculate total quantity with error handling
      const quantity = products.reduce(
        (total_qty: number, curr_product: TMableProduct) =>
          total_qty + (curr_product?.qty || 0),
        0,
      );

      return {
        currency,
        discounts: discounts || [],
        products: products || [],
        quantity: quantity || 0,
        total: {
          discount: totalDiscount || price_fields.total_discounts || 0,
          price: price_fields.total_discounted_price || 0,
          shipping: price_fields.total_shipping_price || 0,
          tax: price_fields.total_tax || 0,
        },
      } satisfies TCartData;
    } catch (error) {
      Logger.error('Error in getCartData', {
        error,
        input: cpx_input_payload,
      });
      // Return safe fallback cart data
      return {
        currency: EURO_CURRENCY_CODE,
        discounts: [],
        products: [],
        quantity: 0,
        total: {
          discount: 0,
          price: 0,
          shipping: 0,
          tax: 0,
        },
      };
    }
  }

  static getDiscountsForCpxCheckout(
    cpx_line_checkout: CpxCheckout | undefined,
  ): { discounts: TDiscount[]; totalDiscount: number } {
    if (cpx_line_checkout) {
      const discounts =
        cpx_line_checkout.discountApplications?.map(
          (d: CpxDiscountApplication) =>
            ({
              code: d.title || '',
              targets: (d.targetSelection || '') as EDiscountTarget,
              targetType: (d.targetType || '') as EDiscountTargetType,
              type: (d.type || '') as EDiscountType,
              value: Object.values(d.value || {})[0] || 0,
              valueType:
                (Object.keys(d.value || {})[0] as EDiscountValueType) || null,
            }) satisfies TDiscount,
        ) || [];
      const totalDiscount = cpx_line_checkout.discountsAmount?.amount || 0;
      return { discounts, totalDiscount };
    }
    return { discounts: [], totalDiscount: 0 };
  }

  static getDiscountsForCpxLineItem(cpx_line_item: CpxLineItem): {
    discounts: TDiscount[];
    totalDiscount: number;
  } {
    if (cpx_line_item) {
      const discounts =
        cpx_line_item.discountAllocations?.map(
          (d: CpxDiscountAllocation) =>
            ({
              code: d.discountApplication?.title || '',
              targets: (d.discountApplication?.targetSelection ||
                '') as EDiscountTarget,
              targetType: (d.discountApplication?.targetType ||
                '') as EDiscountTargetType,
              type: (d.discountApplication?.type || '') as EDiscountType,
              value: Object.values(d.discountApplication?.value || {})[0] || 0,
              valueType:
                (Object.keys(
                  d.discountApplication?.value || {},
                )[0] as EDiscountValueType) || null,
            }) satisfies TDiscount,
        ) || [];
      const totalDiscount =
        cpx_line_item.discountAllocations?.reduce(
          (acc: number, d: CpxDiscountAllocation) =>
            acc + (d?.amount?.amount || 0),
          0,
        ) || 0;
      return { discounts, totalDiscount };
    }
    return { discounts: [], totalDiscount: 0 };
  }

  static getCustomerData(
    cpx_input: ShpyCpxHandlerInput,
  ): ICustomerDataFields[] {
    return CpxCustomerDataHandler.getCombinedCustomerData(cpx_input);
  }

  static getCustomPixelBrowserData(
    cpx_input_payload: ShpyCpxHandlerInput,
  ): TBrowserApiData {
    try {
      const contextData = cpx_input_payload?.event?.context || {};
      const documentData = contextData?.document || {};
      const navigatorData = contextData?.navigator || {};
      const windowData = contextData?.window || {};
      const documentLocationData = documentData?.location || {};

      return {
        cks: CookieLibrary.getAll(),
        dl: documentLocationData?.href || '',
        dt: documentLocationData?.title || documentData?.title || '',
        mp: 0,
        pp: 0,
        qpar: queryParamsFromUrlString(documentLocationData?.href || ''),
        rl: documentData?.referrer || documentLocationData?.referrer || '',
        srh: windowData?.screen?.height,
        srw: windowData?.screen?.width,
        ua: navigatorData?.userAgent,
        ul: navigatorData?.language,
      };
    } catch (error) {
      Logger.error('Error in getCustomPixelBrowserData', {
        error,
        input: cpx_input_payload,
      });
      return {
        cks: CookieLibrary.getAll(),
        dl: '',
        dt: '',
        mp: 0,
        pp: 0,
        qpar: {},
        rl: '',
        srh: 0,
        srw: 0,
        ua: '',
        ul: '',
      };
    }
  }
}
